import { Button } from "components/DesignSystem/Button/Button";
import Popover from "components/DesignSystem/Popover/Popover";
import classNames from "classnames";
import { useState } from "react";
import {
  DIRECTOR_PILL,
  OTHER_PILL,
  PersonaTypeOption,
  RoleDetail,
  SHAREHOLDER_PILL,
} from "./addPersonModal.utils";

const RolePill = ({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) => (
  <Button
    type="button"
    customType="ghost"
    onClick={onClick}
    className={classNames(
      "!t-h-auto !t-select-none !t-rounded-full !t-px-3 !t-py-1 !t-text-body-sm",
      isActive
        ? "!t-bg-purple !t-text-white hover:!t-bg-purple"
        : "!t-bg-neutral-0 !t-text-neutral-70 hover:!t-bg-neutral-0"
    )}
  >
    {label}
  </Button>
);

const CannotRemoveRoleTooltipContent = () => (
  <>
    <p className="t-m-0 t-text-body-sm t-font-bold t-text-white">
      Why can&apos;t I remove this role?
    </p>
    <div className="t-mt-2 t-flex t-flex-col t-gap-3 t-text-body-sm">
      <div className="t-text-white/70">
        Roles can&apos;t be removed directly because it is linked to your
        entity&apos;s records & federal filings.
      </div>
      <div className="t-flex t-flex-col t-gap-2">
        <div>
          <div className="t-text-white">
            1. If this person no longer holds this role
          </div>
          <div className="t-text-white/70">
            Uncheck &quot;This person currently holds this role&quot; and enter
            the date their role ended.
          </div>
        </div>
        <div>
          <div className="t-text-white">
            2. If this role was added by mistake
          </div>
          <div className="t-text-white/70">
            Contact our team and we&apos;ll help remove the incorrect record for
            you.
          </div>
        </div>
      </div>
    </div>
  </>
);

const RolePillWithLock = ({
  label,
  isActive,
  isLocked,
  isTooltipOpen,
  onClick,
  onTooltipOpenChange,
}: {
  label: string;
  isActive: boolean;
  isLocked: boolean;
  isTooltipOpen: boolean;
  onClick: () => void;
  onTooltipOpenChange: (open: boolean) => void;
}) => {
  if (!isLocked) {
    return <RolePill label={label} isActive={isActive} onClick={onClick} />;
  }
  return (
    <Popover.Root open={isTooltipOpen} onOpenChange={onTooltipOpenChange}>
      <Popover.Anchor asChild>
        <span>
          <RolePill label={label} isActive={isActive} onClick={onClick} />
        </span>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="center"
          className="t-z-popover t-w-80"
        >
          <Popover.Arrow className="t-fill-neutral-100" />
          <CannotRemoveRoleTooltipContent />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export const RolePillGroup = ({
  personaTypes,
  selectedPills,
  roleDetails,
  isEditMode,
  isShareholderLocked,
  isOtherPillLocked,
  onTogglePill,
}: {
  personaTypes: PersonaTypeOption[];
  selectedPills: string[];
  roleDetails: Record<string, RoleDetail>;
  isEditMode: boolean;
  isShareholderLocked: boolean;
  isOtherPillLocked: boolean;
  onTogglePill: (pill: string) => void;
}) => {
  const [lockedPillTooltip, setLockedPillTooltip] = useState<string | null>(
    null
  );
  const isDirectorLocked =
    isEditMode && Boolean(roleDetails[DIRECTOR_PILL]?.joinId);

  const handlePillClick = (pill: string, isLocked: boolean) => {
    if (isLocked) {
      setLockedPillTooltip(pill);
      return;
    }
    setLockedPillTooltip(null);
    onTogglePill(pill);
  };

  return (
    <div className="t-flex t-flex-wrap t-gap-1.5">
      {personaTypes.map((type) => {
        const isLocked = isEditMode && Boolean(roleDetails[type.uuid]?.joinId);
        return (
          <RolePillWithLock
            key={type.uuid}
            label={type.name}
            isActive={selectedPills.includes(type.uuid)}
            isLocked={isLocked}
            isTooltipOpen={lockedPillTooltip === type.uuid}
            onTooltipOpenChange={(open) =>
              setLockedPillTooltip(open ? type.uuid : null)
            }
            onClick={() => handlePillClick(type.uuid, isLocked)}
          />
        );
      })}
      <RolePillWithLock
        label="Director"
        isActive={selectedPills.includes(DIRECTOR_PILL)}
        isLocked={isDirectorLocked}
        isTooltipOpen={lockedPillTooltip === DIRECTOR_PILL}
        onTooltipOpenChange={(open) =>
          setLockedPillTooltip(open ? DIRECTOR_PILL : null)
        }
        onClick={() => handlePillClick(DIRECTOR_PILL, isDirectorLocked)}
      />
      <RolePillWithLock
        label="Shareholder"
        isActive={selectedPills.includes(SHAREHOLDER_PILL)}
        isLocked={isShareholderLocked}
        isTooltipOpen={lockedPillTooltip === SHAREHOLDER_PILL}
        onTooltipOpenChange={(open) =>
          setLockedPillTooltip(open ? SHAREHOLDER_PILL : null)
        }
        onClick={() => handlePillClick(SHAREHOLDER_PILL, isShareholderLocked)}
      />
      <RolePillWithLock
        label="Other role(s)"
        isActive={selectedPills.includes(OTHER_PILL)}
        isLocked={isOtherPillLocked}
        isTooltipOpen={lockedPillTooltip === OTHER_PILL}
        onTooltipOpenChange={(open) =>
          setLockedPillTooltip(open ? OTHER_PILL : null)
        }
        onClick={() => handlePillClick(OTHER_PILL, isOtherPillLocked)}
      />
    </div>
  );
};
